import { useState } from 'react';
import { FEEXPAY_WEBHOOK_HEADER_NAME, FEEXPAY_WEBHOOK_HEADER_TYPE } from '@/lib/feexpayWebhook';

interface SecurityCardProps {
  secretConfigured: boolean;
  statusError: string | null;
}

/** Génère une valeur d'en-tête robuste (sans dépendance externe). */
function generateSecret(): string {
  const bytes = new Uint8Array(24);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  const hex = Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
  return `zifek_wh_${hex}`;
}

export default function SecurityCard({ secretConfigured, statusError }: SecurityCardProps) {
  const [suggestion, setSuggestion] = useState('');
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(suggestion);
    } catch {
      /* presse-papiers indisponible */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="rounded-lg border border-background-200/70 bg-background-50 p-5">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-secondary-100 text-secondary-900 flex items-center justify-center flex-shrink-0">
          <i className="ri-shield-keyhole-line text-lg"></i>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-bold text-foreground-950">Authentification</h3>
            <span
              className={`text-[10px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${
                secretConfigured ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
              }`}
            >
              {secretConfigured ? 'Secret actif' : 'Secret non configuré'}
            </span>
          </div>
          <p className="text-[11px] text-foreground-500 mt-0.5">
            FeexPay signe chaque appel avec l’en-tête défini ci-dessous.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-[11px] font-semibold text-foreground-600 mb-1.5">Type d’en-tête</label>
          <div className="h-10 px-3 rounded-md border border-background-200/70 bg-background-100 flex items-center gap-2">
            <span className="text-xs font-mono font-semibold text-foreground-800">{FEEXPAY_WEBHOOK_HEADER_TYPE}</span>
            <span className="text-[10px] text-foreground-400">({FEEXPAY_WEBHOOK_HEADER_NAME})</span>
          </div>
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-foreground-600 mb-1.5">Valeur de l’en-tête</label>
          <div className="h-10 px-3 rounded-md border border-background-200/70 bg-background-100 flex items-center justify-between gap-2">
            <span className="text-xs font-mono text-foreground-700">
              {secretConfigured ? '•••••••• stocké côté serveur' : 'aucun secret'}
            </span>
            <i className={`text-sm ${secretConfigured ? 'ri-lock-2-line text-green-600' : 'ri-lock-unlock-line text-amber-600'}`}></i>
          </div>
        </div>
      </div>

      {!secretConfigured && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/70 p-3">
          <p className="text-[11px] font-semibold text-amber-900 flex items-start gap-1.5">
            <i className="ri-error-warning-line mt-0.5"></i>
            Sans secret, l’endpoint accepte n’importe quel appel : quelqu’un pourrait simuler un paiement réussi.
          </p>
          <ol className="mt-2 space-y-1 text-[11px] text-foreground-700 list-decimal list-inside">
            <li>Générez une valeur ci-dessous et copiez-la.</li>
            <li>Enregistrez-la comme secret serveur `FEEXPAY_WEBHOOK_SECRET` (formulaire sécurisé, jamais dans le chat).</li>
            <li>Collez exactement la même valeur dans FeexPay → Webhook → Valeur de l’en-tête.</li>
          </ol>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setSuggestion(generateSecret())}
              className="h-8 px-3 rounded-full bg-foreground-950 text-background-50 text-[11px] font-semibold whitespace-nowrap flex items-center gap-1.5 hover:bg-foreground-800 transition-colors cursor-pointer"
            >
              <i className="ri-key-2-line"></i>
              Générer une valeur
            </button>
            {suggestion && (
              <>
                <code className="h-8 px-3 rounded-md border border-background-200/70 bg-background-50 text-[11px] font-mono text-foreground-800 flex items-center overflow-x-auto whitespace-nowrap">
                  {suggestion}
                </code>
                <button
                  type="button"
                  onClick={copy}
                  className="h-8 px-3 rounded-full border border-background-200/70 text-[11px] font-medium text-foreground-600 whitespace-nowrap flex items-center gap-1.5 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                  {copied ? 'Copié' : 'Copier'}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {statusError && (
        <p className="mt-3 text-[11px] text-amber-700 bg-amber-50/70 border border-amber-200 rounded-md px-3 py-2 flex items-start gap-1.5">
          <i className="ri-plug-line mt-0.5"></i>
          Vérification de l’état impossible : {statusError}
        </p>
      )}

      {secretConfigured && (
        <p className="mt-3 text-[11px] text-foreground-600 bg-background-100 border border-background-200/70 rounded-md px-3 py-2 flex items-start gap-1.5">
          <i className="ri-checkbox-circle-line text-green-600 mt-0.5"></i>
          Le secret est stocké côté serveur (jamais exposé au navigateur). Un appel sans le bon en-tête
          <span className="font-mono mx-1">Authorization: Bearer …</span>
          est journalisé puis rejeté en 401.
        </p>
      )}
    </div>
  );
}